import { PostV3GfwTeamsTeamIdUsersError, usePostV3GfwTeamsTeamIdUsers } from "generated/core/coreComponents";
import { useInvalidateGetUserTeams } from "hooks/querys/teams/useGetUserTeams";
import { useInvalidateUsersProfileBatch } from "hooks/querys/users/useGetUsersProfileBatch";
import { useAccessToken } from "hooks/useAccessToken";
import { FC, useMemo, useState, useEffect } from "react";
import Modal from "components/ui/Modal/Modal";
import { useHistory, useParams } from "react-router-dom";
import { TParams as TTeamDetailParams } from "../TeamDetail";
import { useIntl, FormattedMessage } from "react-intl";
import { toastr } from "react-redux-toastr";
import { fireGAEvent } from "helpers/analytics";
import { TeamActions, TeamLabels } from "types/analytics";
import { profileService, TUserProfile } from "services/profile";
import { useQuery } from "@tanstack/react-query";
import CreatableSelect from "react-select/creatable";

type TParams = TTeamDetailParams & {
  memberRole: "manager" | "monitor";
};

type TUserSelectOption = {
  value: string;
  label: string;
  name: string;
  email: string;
};

interface IProps {
  isOpen: boolean;
}

const AddTeamMemberModal: FC<IProps> = props => {
  const { isOpen } = props;
  const { teamId, memberRole } = useParams<TParams>();
  const intl = useIntl();
  const history = useHistory();

  const [selectedOption, setSelectedOption] = useState<TUserSelectOption | null>(null);
  const [typedEmail, setTypedEmail] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  /* Mutations */
  const { httpAuthHeader } = useAccessToken();
  const { mutateAsync: addTeamMembers } = usePostV3GfwTeamsTeamIdUsers();
  const invalidateGetUserTeams = useInvalidateGetUserTeams();
  const invalidateUsersProfileBatch = useInvalidateUsersProfileBatch();

  /* Query all registered profiles directly from our Profile API */
  const { data: profilesResponse, isLoading: isLoadingProfiles } = useQuery(
    ["allUserProfiles"],
    () => profileService.getAllUserProfiles(),
    {
      enabled: isOpen,
      staleTime: 1000 * 5
    }
  );

  const userOptions = useMemo<TUserSelectOption[]>(() => {
    const list: TUserProfile[] = profilesResponse?.data || [];
    return list
      .filter(user => Boolean(user.profileData?.email))
      .map(user => {
        const firstName = user.profileData?.firstName || "";
        const lastName = user.profileData?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        const email = user.profileData!.email!;

        return {
          value: email,
          label: fullName ? `${fullName} (${email})` : email,
          name: fullName,
          email
        };
      });
  }, [profilesResponse]);

  useEffect(() => {
    // In case the URL ends in anything else: /teams/:teamId/add/:memberRole
    if (isOpen && memberRole && memberRole !== "manager" && memberRole !== "monitor") {
      history.push(`/teams/${teamId}`);
    }
  }, [history, memberRole, isOpen, teamId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedOption(null);
      setTypedEmail("");
      setValidationError("");
    }
  }, [isOpen]);

  const onClose = () => {
    history.push(`/teams/${teamId}`);
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const onSave = async () => {
    const emailToSubmit = (selectedOption?.value || typedEmail || "").trim();

    if (!emailToSubmit) {
      setValidationError(
        intl.formatMessage({ id: "teams.field.email.placeholder" }) || "Please enter an email address."
      );
      return;
    }

    if (!validateEmail(emailToSubmit)) {
      setValidationError("Please enter a valid email address.");
      return;
    }

    setValidationError("");

    try {
      await addTeamMembers({
        headers: httpAuthHeader,
        pathParams: {
          teamId
        },
        body: [
          {
            email: emailToSubmit,
            role: memberRole
          }
        ]
      });

      // Ensure Team Listing, Team Details, and Batch Profiles are invalidated
      await invalidateGetUserTeams(teamId);
      await invalidateUsersProfileBatch();

      toastr.success(intl.formatMessage({ id: "teams.details.add.member.success" }), "");
      fireGAEvent({
        category: "Teams",
        action: TeamActions.teamManagement,
        label: TeamLabels.AddedMonitor
      });
      onClose();
    } catch (e: any) {
      const error = e as PostV3GfwTeamsTeamIdUsersError;
      toastr.error(
        intl.formatMessage({ id: "teams.details.add.member.error" }),
        typeof error.payload === "string" ? "" : error.payload?.message || "Error adding team member"
      );
      console.error(error);
    }
  };

  const modalTitle = memberRole ? `teams.add.${memberRole}` : "common.add";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      actions={[
        {
          name: "common.cancel",
          variant: "secondary",
          onClick: onClose
        },
        {
          name: "common.add",
          variant: "primary",
          onClick: onSave
        }
      ]}
    >
      <div className="space-y-4 py-2">
        <div className="c-input c-input--alt-label">
          <label htmlFor="team-member-creatable-select" className="c-input__label c-input__label--alt">
            <FormattedMessage id="teams.field.email" />
          </label>

          <div className="c-input__input-wrapper">
            <CreatableSelect<TUserSelectOption>
              inputId="team-member-creatable-select"
              isClearable
              isLoading={isLoadingProfiles}
              value={selectedOption}
              options={userOptions}
              placeholder={intl.formatMessage({ id: "teams.field.email.placeholder" }) || "Type or select email..."}
              onChange={newValue => {
                setSelectedOption(newValue);
                if (newValue) {
                  setTypedEmail(newValue.value);
                  setValidationError("");
                } else {
                  setTypedEmail("");
                }
              }}
              onInputChange={inputValue => {
                setTypedEmail(inputValue);
                if (inputValue) setValidationError("");
              }}
              formatCreateLabel={inputValue => `Use custom email "${inputValue}"`}
              formatOptionLabel={(option: TUserSelectOption) => (
                <div className="py-0.5">
                  <span className="font-semibold text-neutral-800 text-sm">{option.name || option.email}</span>
                  {option.name && <span className="text-neutral-600 text-sm ml-2">({option.email})</span>}
                </div>
              )}
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: "#ffffff",
                  borderColor: validationError ? "#ed1846" : state.isFocused ? "#97be32" : "#555555",
                  boxShadow: state.isFocused ? "0 0 0 1px #97be32" : "none",
                  "&:hover": {
                    borderColor: state.isFocused ? "#97be32" : "#333333"
                  },
                  borderRadius: "4px",
                  minHeight: "44px",
                  fontFamily: '"Fira Sans", sans-serif',
                  fontSize: "15px"
                }),
                input: base => ({
                  ...base,
                  color: "#333333",
                  fontFamily: '"Fira Sans", sans-serif'
                }),
                singleValue: base => ({
                  ...base,
                  color: "#333333",
                  fontFamily: '"Fira Sans", sans-serif',
                  fontWeight: 500
                }),
                placeholder: base => ({
                  ...base,
                  color: "#555555",
                  fontFamily: '"Fira Sans", sans-serif'
                }),
                menu: base => ({
                  ...base,
                  zIndex: 999999,
                  fontFamily: '"Fira Sans", sans-serif',
                  borderRadius: "4px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #555555",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)"
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#e9f2d3" : state.isFocused ? "#f5f5f5" : "#ffffff",
                  color: "#333333",
                  cursor: "pointer",
                  padding: "10px 14px",
                  borderBottom: "1px solid #f0f0f0"
                })
              }}
            />
          </div>

          {/* Validation error */}
          {validationError && <p className="text-xs text-red-600 font-medium mt-1">{validationError}</p>}
        </div>
      </div>
    </Modal>
  );
};

export default AddTeamMemberModal;
